//
// Edge↔Centre radial stepper with outward ray seeding and panel-driven linger.
// Semantics (aligned with GravityPanel UI):
//   speed < 0 : EDGE  → CENTRE  (inward)
//   speed > 0 : CENTRE→ EDGE    (outward)
//   speed = 0 : FREEZE
//
// When target reached, particle will either freeze and linger (if gravityLingerS>0)
// or be marked dead immediately. SimpleEmitter removes dead ones.
//

const EPS_CENTRE_PX_DEFAULT = 0.75; // fallback if no UI value

/* ----------------------- boundary helpers ----------------------- */
function isRect(b) { return !!b && (b.type === "rect" || (b.rect && typeof b.rect === "object")); }
function isCircle(b) { return !!b && (b.type === "circle" || (Number.isFinite(b.r) || Number.isFinite(b.radius))); }

function rectData(b, imageRect) {
  if (b?.rect) return b.rect;
  return { x: imageRect?.x || 0, y: imageRect?.y || 0, width: imageRect?.width || 0, height: imageRect?.height || 0 };
}

function circleData(b, centre, pxPerMeter) {
  const r = Number.isFinite(b?.r) ? b.r : Number.isFinite(b?.radius) ? b.radius : (pxPerMeter || 80) * 0.5;
  return { cx: centre.x, cy: centre.y, r: Math.max(1, r) };
}

function clampToRectEdgeFromCentre(rect, centre, pos) {
  const dirx = pos.x - centre.x;
  const diry = pos.y - centre.y;
  const EPS = 1e-6;
  const vx = Math.abs(dirx) < EPS ? (dirx >= 0 ? EPS : -EPS) : dirx;
  const vy = Math.abs(diry) < EPS ? (diry >= 0 ? EPS : -EPS) : diry;

  const left   = rect.x;
  const right  = rect.x + rect.width;
  const top    = rect.y;
  const bottom = rect.y + rect.height;

  const tx1 = (left   - centre.x) / vx;
  const tx2 = (right  - centre.x) / vx;
  const ty1 = (top    - centre.y) / vy;
  const ty2 = (bottom - centre.y) / vy;

  const candidates = [tx1, tx2, ty1, ty2].filter((t) => t > 0);
  const t = candidates.length ? Math.min(...candidates) : 0;

  return { x: centre.x + vx * t, y: centre.y + vy * t };
}

function pointOnCircleFromCentre(circ, pos) {
  const dx = pos.x - circ.cx, dy = pos.y - circ.cy;
  const len = Math.hypot(dx, dy) || 1;
  return { x: circ.cx + (dx / len) * circ.r, y: circ.cy + (dy / len) * circ.r };
}

function isOutsideRect(rect, p) {
  return p.x <= rect.x || p.x >= rect.x + rect.width || p.y <= rect.y || p.y >= rect.y + rect.height;
}

function moveToward(a, b, step) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  if (dist <= step || dist === 0) return { x: b.x, y: b.y, arrived: true, dist };
  const k = step / dist;
  return { x: a.x + dx * k, y: a.y + dy * k, arrived: false, dist };
}

function norm2(x, y) { return Math.hypot(x, y); }

/* ---------------------- linger helpers ---------------------- */
function startLingerIfAny(p, lingerS) {
  const L = Math.max(0, Number(lingerS || 0));
  if (L > 0) {
    p.__gravFrozen = true;
    p.__freezeX = p.cx; p.__freezeY = p.cy;
    p.__gravLingerRemainS = (p.__gravLingerRemainS ?? L);
    p.vx = 0; p.vy = 0;
    return true; // frozen, not dead
  }
  p.__dead = true;
  return false;
}

/* ------------------------- main stepper ------------------------- */
export function applyRadialEdgeCentreStep(p, dt, ctx) {
  const es = ctx?.effectSettings || {};
  if (!es.gravityEnabled) return 0;

  let spdMps = Number(es.gravitySpeed);
  if (!Number.isFinite(spdMps)) spdMps = 0;

  // FREEZE (gravity on but no speed)
  if (spdMps === 0) {
    p.vx = 0; p.vy = 0;
    return 1;
  }

  const ppm = Math.max(1, Number(ctx?.pxPerMeter) || 80);

  // Kill/stop epsilon
  let targetEpsPx = EPS_CENTRE_PX_DEFAULT;
  if (String(es.lifespanMode || "time").toLowerCase() === "to_target") {
    const m = Number(es.targetKillDistMeters);
    if (Number.isFinite(m) && m >= 0) targetEpsPx = Math.max(0, m) * ppm;
  }

  const lingerS = Math.max(0, Number(es.gravityLingerS || 0));

  const speedPx = Math.abs(spdMps) * ppm;
  const centre = ctx?.centre || { x: 0, y: 0 };
  const boundary = ctx?.boundary || null;

  // If already frozen by gravity linger, pin and count down here
  if (p.__gravFrozen) {
    p.cx = p.__freezeX; p.cy = p.__freezeY;
    p.vx = 0; p.vy = 0;
    p.__gravLingerRemainS = Math.max(0, (p.__gravLingerRemainS ?? lingerS) - dt);
    if (p.__gravLingerRemainS <= 0) p.__dead = true;
    return 1;
  }

  if (!p.__gravInit) {
    p.__gravInit = true;

    if (spdMps < 0) {
      // inward (edge → centre): snap to edge (based on current pos ray)
      if (isRect(boundary)) {
        const rect = rectData(boundary, ctx?.imageRect);
        const snapped = clampToRectEdgeFromCentre(rect, centre, { x: p.cx, y: p.cy });
        p.cx = snapped.x; p.cy = snapped.y;
      } else if (isCircle(boundary)) {
        const circ = circleData(boundary, centre, ppm);
        const snapped = pointOnCircleFromCentre(circ, { x: p.cx, y: p.cy });
        p.cx = snapped.x; p.cy = snapped.y;
      }
    } else {
      // outward (centre → edge): snap to centre and seed a ray direction
      p.cx = centre.x; p.cy = centre.y;

      let dx = p.vx || 0, dy = p.vy || 0;
      let n = norm2(dx, dy);
      if (n < 1e-6) {
        const ang = Math.random() * Math.PI * 2;
        dx = Math.cos(ang);
        dy = Math.sin(ang);
        n = 1;
      }
      p.__gravDir = { x: dx / n, y: dy / n };
    }
  }

  if (spdMps < 0) {
    // inward: EDGE → CENTRE
    const step = speedPx * Math.max(0, dt);
    const next = moveToward({ x: p.cx, y: p.cy }, centre, step);
    p.cx = next.x; p.cy = next.y;
    if (next.arrived || next.dist <= targetEpsPx) {
      startLingerIfAny(p, lingerS);
    }
    return 1;
  } else {
    // outward: CENTRE → EDGE
    let edgePoint = null;

    if (isRect(boundary)) {
      const rect = rectData(boundary, ctx?.imageRect);
      if (p.__gravDir) {
        const far = 1e6;
        const probe = { x: centre.x + p.__gravDir.x * far, y: centre.y + p.__gravDir.y * far };
        edgePoint = clampToRectEdgeFromCentre(rect, centre, probe);
      } else {
        edgePoint = clampToRectEdgeFromCentre(rect, centre, { x: p.cx, y: p.cy });
      }
      const step = speedPx * Math.max(0, dt);
      const next = moveToward({ x: p.cx, y: p.cy }, edgePoint, step);
      p.cx = next.x; p.cy = next.y;

      if (next.arrived || isOutsideRect(rect, p) || next.dist <= targetEpsPx) {
        startLingerIfAny(p, lingerS);
      }
      return 1;
    }

    if (isCircle(boundary)) {
      const circ = circleData(boundary, centre, ppm);
      const target = p.__gravDir
        ? { x: circ.cx + p.__gravDir.x * circ.r, y: circ.cy + p.__gravDir.y * circ.r }
        : pointOnCircleFromCentre(circ, { x: p.cx, y: p.cy });
      const step = speedPx * Math.max(0, dt);
      const next = moveToward({ x: p.cx, y: p.cy }, target, step);
      p.cx = next.x; p.cy = next.y;

      const d = Math.hypot(p.cx - circ.cx, p.cy - circ.cy);
      if (next.arrived || Math.abs(d - circ.r) <= targetEpsPx) {
        startLingerIfAny(p, lingerS);
      }
      return 1;
    }

    // Unknown boundary: march straight out and linger at the far target
    const dir = p.__gravDir || (() => {
      const dx = p.cx - centre.x, dy = p.cy - centre.y, n = norm2(dx, dy) || 1;
      return { x: dx / n, y: dy / n };
    })();
    const far = 1e6;
    const target = { x: centre.x + dir.x * far, y: centre.y + dir.y * far };
    const step = speedPx * Math.max(0, dt);
    const next = moveToward({ x: p.cx, y: p.cy }, target, step);
    p.cx = next.x; p.cy = next.y;
    if (next.arrived) startLingerIfAny(p, lingerS);
    return 1;
  }
}
